import React from 'react';
import { Pressable } from 'react-native';

import { Box, Text, useTheme } from 'app/shopify/theme';

type DateState = 'enabled' | 'hover' | 'pressed' | 'disabled' | 'null' | 'conflict';
type DateType = 'standalone' | 'start' | 'middle' | 'end';

export interface DateProps {
  digit?: string;
  selected?: boolean;
  state?: DateState;
  type?: DateType;
  onPress?: () => void;
}

interface PressableInteractionState {
  pressed: boolean;
  hovered?: boolean;
}

const DATE_SIZE = 40;

export function Date({ digit = '2', selected = false, state = 'enabled', type = 'standalone', onPress }: DateProps) {
  const theme = useTheme();
  const isNull = state === 'null';
  const isDisabled = state === 'disabled';
  const isInteractive = !isDisabled && !isNull;

  function getContainerBackground(showHover: boolean, showPressed: boolean) {
    if (isNull) return 'transparent';
    if (selected && state === 'conflict') return theme.colors['surface.error.subtle'];
    if (selected && (type === 'middle' || type === 'start' || type === 'end'))
      return theme.colors['surface.brand.subtle'];
    if (selected && type === 'standalone') return theme.colors['surface.brand.strong'];
    if (isDisabled) return theme.colors['surface.disabled'];
    if (showPressed || state === 'pressed') return theme.colors['state-layer.press-brand'];
    if (showHover || state === 'hover') return theme.colors['state-layer.hover-brand'];
    return 'transparent';
  }

  function getDigitColor() {
    if (isNull) return 'transparent';
    if (isDisabled) return theme.colors['text.disabled'];
    if (selected && state === 'conflict') return theme.colors['text.error'];
    if (selected && (type === 'middle' || type === 'start' || type === 'end')) return theme.colors['text.brand'];
    if (selected && type === 'standalone') return theme.colors['text.inverse.primary'];
    return theme.colors['text.primary'];
  }

  function renderCircle(showHover: boolean, showPressed: boolean) {
    const circleBackground = selected ? theme.colors['surface.brand.strong'] : 'transparent';
    const showStateLayer =
      selected && type === 'standalone' && (state === 'hover' || state === 'pressed' || showHover || showPressed);
    const stateLayerColor =
      showPressed || state === 'pressed'
        ? theme.colors['state-layer.press-brand']
        : theme.colors['state-layer.hover-brand'];

    return (
      <Box
        width={DATE_SIZE}
        height={DATE_SIZE}
        borderRadius="radius.full"
        alignItems="center"
        justifyContent="center"
        style={{ backgroundColor: circleBackground }}
      >
        {showStateLayer ? (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            borderRadius="radius.full"
            style={{ backgroundColor: stateLayerColor }}
          />
        ) : null}

        <Text
          variant="bodyBaseMedium"
          style={{ color: selected ? theme.colors['text.inverse.primary'] : getDigitColor() }}
        >
          {digit}
        </Text>
      </Box>
    );
  }

  function renderInner(interaction?: PressableInteractionState) {
    const pressed = interaction?.pressed ?? false;
    const hovered = interaction?.hovered ?? false;
    const showPressed = isInteractive && pressed;
    const showHover = isInteractive && hovered && !pressed;

    if (isNull) {
      return <Box width={DATE_SIZE} height={DATE_SIZE} borderRadius="radius.full" />;
    }

    if (selected && state === 'enabled' && type === 'middle') {
      return (
        <Box
          width={DATE_SIZE}
          height={DATE_SIZE}
          alignItems="center"
          justifyContent="center"
          style={{ backgroundColor: theme.colors['surface.brand.subtle'] }}
        >
          <Text variant="bodyBaseMedium" style={{ color: theme.colors['text.emphasized'] }}>
            {digit}
          </Text>
        </Box>
      );
    }

    if (selected && state === 'enabled' && (type === 'start' || type === 'end')) {
      return (
        <Box width={DATE_SIZE} height={DATE_SIZE} flexDirection="row" alignItems="center" justifyContent="center">
          <Box
            flex={1}
            height={DATE_SIZE}
            style={{ backgroundColor: type === 'end' ? theme.colors['surface.brand.subtle'] : 'transparent' }}
          />
          <Box
            flex={1}
            height={DATE_SIZE}
            style={{ backgroundColor: type === 'start' ? theme.colors['surface.brand.subtle'] : 'transparent' }}
          />
          <Box position="absolute" top={0} left={0} right={0} bottom={0} alignItems="center" justifyContent="center">
            <Box
              width={DATE_SIZE}
              height={DATE_SIZE}
              borderRadius="radius.full"
              alignItems="center"
              justifyContent="center"
              style={{ backgroundColor: theme.colors['surface.brand.strong'] }}
            >
              <Text variant="bodyBaseMedium" style={{ color: theme.colors['text.inverse.primary'] }}>
                {digit}
              </Text>
            </Box>
          </Box>
        </Box>
      );
    }

    if (selected && (type === 'start' || type === 'end')) {
      return (
        <Box width={DATE_SIZE} height={DATE_SIZE} flexDirection="row" alignItems="center" justifyContent="center">
          <Box
            flex={1}
            height={DATE_SIZE}
            style={{ backgroundColor: type === 'end' ? theme.colors['surface.brand.subtle'] : 'transparent' }}
          />
          <Box
            flex={1}
            height={DATE_SIZE}
            style={{ backgroundColor: type === 'start' ? theme.colors['surface.brand.subtle'] : 'transparent' }}
          />
          <Box position="absolute" top={0} left={0} right={0} bottom={0} alignItems="center" justifyContent="center">
            {renderCircle(showHover, showPressed)}
          </Box>
        </Box>
      );
    }

    return (
      <Box
        width={DATE_SIZE}
        height={DATE_SIZE}
        borderRadius="radius.full"
        alignItems="center"
        justifyContent="center"
        style={{ backgroundColor: getContainerBackground(showHover, showPressed) }}
      >
        <Text variant="bodyBaseMedium" style={{ color: getDigitColor() }}>
          {digit}
        </Text>
      </Box>
    );
  }

  if (isInteractive) {
    return (
      <Pressable onPress={onPress}>
        {(pressableState) =>
          renderInner({
            pressed: pressableState.pressed,
            hovered: 'hovered' in pressableState ? (pressableState as PressableInteractionState).hovered : false,
          })
        }
      </Pressable>
    );
  }

  return <Box>{renderInner()}</Box>;
}
